import { useEffect, useState } from "react";
import { Palette, Check, Lock, Pencil, ShieldCheck, ShieldOff, KeyRound, Copy } from "lucide-react";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";
import { toast } from "sonner";
import { setCurrencyGainToastSuppression } from "@/lib/currencyToastGate";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader } from "@/components/Page";
import { Banner, Sigil, RARITY_META } from "@/lib/cosmetics";
import { ProfilePictureSection } from "@/components/ProfilePicture";
import Modal from "@/components/Modal";

export default function LudusCustomize() {
  const { refreshUser, user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("banner");
  const [busy, setBusy] = useState("");
  const [renameName, setRenameName] = useState("");
  const [renameMode, setRenameMode] = useState(false);
  const [confirmPurchase, setConfirmPurchase] = useState(null); // { item, type }
  
  // 2FA state
  const [twoFAStatus, setTwoFAStatus] = useState(null);
  const [twoFASetup, setTwoFASetup] = useState(null);
  const [twoFACode, setTwoFACode] = useState("");
  const [twoFADisableCode, setTwoFADisableCode] = useState("");
  const [twoFADisableModal, setTwoFADisableModal] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [twoFABusy, setTwoFABusy] = useState(false);
  const [suppressCurrencyGainToasts, setSuppressCurrencyGainToasts] = useState(Boolean(user?.suppress_currency_gain_toasts));

  const load = () => api.get("/cosmetics").then((r) => setData(r.data)).catch(() => setData(false));
  const loadTwoFAStatus = () => api.get("/settings/2fa/status").then((r) => setTwoFAStatus(r.data)).catch(() => setTwoFAStatus({ enabled: false }));
  
  useEffect(() => { 
    load(); 
    loadTwoFAStatus();
  }, []);

  useEffect(() => {
    setSuppressCurrencyGainToasts(Boolean(user?.suppress_currency_gain_toasts));
  }, [user?.suppress_currency_gain_toasts]);

  if (data === null) return <Loader />;
  if (data === false) return <PageHeader title="Customize Ludus" icon={Palette} />;

  const isBanner = tab === "banner";
  const items = isBanner ? data.banners : data.sigils;
  const owned = isBanner ? data.owned_banners : data.owned_sigils;
  const equipped = isBanner ? data.banner : data.sigil;

  const buy = async (item) => {
    setBusy(item.id);
    try { 
      const r = await api.post("/cosmetics/buy", { type: tab, id: item.id }); 
      toast.success(`${item.name} unlocked!`, { icon: <GoldCoin size={18} className="text-amber-400" /> }); 
      await load(); 
      setConfirmPurchase(null); // Close modal
    }
    catch (e) { toast.error(apiErr(e)); } 
    finally { setBusy(""); }
  };
  const equip = async (id) => {
    setBusy(id || "none");
    try { await api.post("/cosmetics/equip", { type: tab, id }); toast.success(id ? "Equipped!" : "Removed"); await load(); await refreshUser(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const doRename = async () => {
    if (!renameName.trim()) { toast.error("Name cannot be empty"); return; }
    setBusy("rename");
    try {
      const { data: result } = await api.put("/settings/rename-lanista", { name: renameName.trim() });
      toast.success("Lanista name changed!");
      await refreshUser();
      setRenameMode(false);
      setRenameName("");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  // 2FA handlers
  const beginTwoFASetup = async () => {
    setTwoFABusy(true);
    try {
      const { data: setup } = await api.post("/settings/2fa/setup");
      setTwoFASetup(setup);
      setBackupCodes(null);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setTwoFABusy(false);
    }
  };

  const enableTwoFA = async () => {
    setTwoFABusy(true);
    try {
      const { data: result } = await api.post("/settings/2fa/enable", { 
        code: twoFACode.trim(),
        secret: twoFASetup.secret
      });
      setBackupCodes(result.backup_codes);
      setTwoFASetup(null);
      setTwoFACode("");
      toast.success("Two-factor authentication enabled");
      await loadTwoFAStatus();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setTwoFABusy(false);
    }
  };

  const disableTwoFA = async () => {
    setTwoFABusy(true);
    try {
      await api.post("/settings/2fa/disable", { code: twoFADisableCode.trim() });
      setTwoFADisableCode("");
      setTwoFADisableModal(false);
      setBackupCodes(null);
      toast.success("Two-factor authentication disabled");
      await loadTwoFAStatus();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setTwoFABusy(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard?.writeText((backupCodes || []).join("\n"));
    toast.success("Backup codes copied to clipboard");
  };

  const updateCurrencyToastPreference = async (nextValue) => {
    setBusy("currency-toasts");
    try {
      await api.post("/settings/currency-gain-toasts", { suppress_currency_gain_toasts: nextValue });
      setSuppressCurrencyGainToasts(nextValue);
      setCurrencyGainToastSuppression(nextValue);
      await refreshUser();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy("");
    }
  };

  return (
    <div>
      <PageHeader title="Customize Ludus" subtitle="Unlock banners & sigils with gold to display on your profile" icon={Palette}
        action={<span className="inline-flex items-center gap-1.5 text-amber-300 border border-amber-900/40 px-3 py-2 bg-zinc-950" data-testid="cosmetics-gold"><GoldCoin size={15} className="text-amber-500" /> {data.gold.toLocaleString()} gold</span>} />

      {/* Profile Avatar Section */}
      <div className="mb-8">
        <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3">Profile Avatar</h3>
        <ProfilePictureSection 
          userId={user?.user_id}
          isOwnProfile={true}
          userName={user?.username}
        />
      </div>

      <div data-testid="currency-gain-toast-preference" className="mb-8 border border-zinc-800 bg-zinc-950 p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-cinzel text-sm uppercase tracking-wide text-amber-300">Currency gain pop-ups</h3>
            <p className="mt-1 text-sm text-zinc-400">Hide every green Gold or Follis reward banner.</p>
          </div>
          <button
            data-testid="currency-gain-toast-toggle"
            type="button"
            role="switch"
            aria-checked={suppressCurrencyGainToasts}
            disabled={busy === "currency-toasts"}
            onClick={() => updateCurrencyToastPreference(!suppressCurrencyGainToasts)}
            className={`inline-flex min-w-28 items-center justify-center border px-4 py-2 text-xs font-cinzel uppercase tracking-wide transition-colors disabled:opacity-50 ${
              suppressCurrencyGainToasts
                ? "border-emerald-600 bg-emerald-950/30 text-emerald-300"
                : "border-zinc-700 text-zinc-300 hover:border-amber-700 hover:text-amber-300"
            }`}
          >
            {busy === "currency-toasts" ? "Saving..." : suppressCurrencyGainToasts ? "Disabled" : "Enabled"}
          </button>
        </div>
      </div>

      {/* Lanista Name Section */}
      <div className="mb-8">
        <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3">Lanista Name</h3>
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
          {!renameMode ? (
            <div className="flex items-center justify-between">
              <div>
                <div className="text-zinc-100 font-cinzel text-lg">{user?.name}</div>
                {user?.name_changed ? (
                  <div className="text-xs text-zinc-500 mt-1">
                    ✓ Name change used (one-time only)
                  </div>
                ) : (
                  <div className="text-xs text-amber-400 mt-1">
                    One free name change available
                  </div>
                )}
              </div>
              {/* Only show rename button if name_changed is explicitly false or undefined, but NOT if it's true */}
              {(user?.name_changed !== true) && (
                <button
                  data-testid="rename-btn"
                  onClick={() => { setRenameMode(true); setRenameName(user?.name || ""); }}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-cinzel uppercase tracking-wide border border-amber-900/50 text-amber-300 hover:border-amber-500 rounded-sm transition-colors"
                >
                  <Pencil size={14} /> Rename
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-zinc-400 block mb-1">New Lanista Name</label>
                <input
                  type="text"
                  data-testid="rename-input"
                  value={renameName}
                  onChange={(e) => setRenameName(e.target.value)}
                  maxLength={40}
                  placeholder="Enter new name..."
                  className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm rounded-sm focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div className="text-xs text-zinc-500">
                <p className="mb-1">• This is a <span className="text-amber-400 font-semibold">one-time change</span> and cannot be undone</p>
                <p>• Name must be 2-40 characters and unique</p>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="rename-confirm-btn"
                  onClick={doRename}
                  disabled={busy === "rename" || !renameName.trim()}
                  className="flex-1 px-4 py-2 text-sm font-cinzel uppercase tracking-wide bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {busy === "rename" ? "Renaming..." : "Confirm"}
                </button>
                <button
                  data-testid="rename-cancel-btn"
                  onClick={() => { setRenameMode(false); setRenameName(""); }}
                  disabled={busy === "rename"}
                  className="px-4 py-2 text-sm font-cinzel uppercase tracking-wide border border-zinc-700 text-zinc-400 hover:border-zinc-500 rounded-sm disabled:opacity-40 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Two-Factor Authentication Section */}
      <div className="mb-8">
        <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3">Two-Factor Authentication</h3>
        <div className={`bg-zinc-950 border rounded-sm p-4 ${twoFAStatus?.enabled ? "border-emerald-500/60 bg-emerald-950/10" : "border-zinc-800"}`}>
          {twoFAStatus?.enabled ? (
            <>
              {/* 2FA Enabled State */}
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck size={18} className="text-emerald-400" />
                <span className="text-sm text-emerald-300 font-semibold">Two-Factor Authentication Enabled</span>
                <span className="ml-auto text-xs text-emerald-400">
                  <KeyRound size={12} className="inline mr-1" />
                  {twoFAStatus.backup_codes_remaining} backup codes left
                </span>
              </div>
              
              {backupCodes && (
                <div className="mb-4 border border-amber-700/50 bg-amber-950/20 p-3 rounded-sm">
                  <p className="text-sm text-amber-200 mb-2 font-semibold">
                    Save these backup codes somewhere safe — they won&apos;t be shown again.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono text-sm text-amber-100 mb-2">
                    {backupCodes.map((code) => (
                      <span key={code} className="bg-zinc-900 border border-zinc-700 px-2 py-1 rounded-sm text-center tracking-wider">
                        {code}
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={copyBackupCodes}
                    className="flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 underline"
                  >
                    <Copy size={12} /> Copy all codes
                  </button>
                </div>
              )}

              <p className="text-sm text-zinc-400 mb-3">
                Your login is protected by an authenticator app. To disable it, you&apos;ll need to confirm with a current code or backup code.
              </p>
              
              <button
                onClick={() => setTwoFADisableModal(true)}
                className="px-4 py-2 border border-red-900/50 text-red-300 hover:border-red-500 text-sm rounded-sm"
              >
                Disable 2FA
              </button>
            </>
          ) : (
            <>
              {/* 2FA Disabled State */}
              {!twoFASetup ? (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldOff size={18} className="text-zinc-500" />
                    <span className="text-sm text-zinc-300">Two-Factor Authentication Disabled</span>
                  </div>
                  <p className="text-sm text-zinc-400 mb-4">
                    Add an extra layer of security to your account by requiring a 6-digit code from an authenticator app (Google Authenticator, Authy, etc.) when you log in.
                  </p>
                  <button
                    onClick={beginTwoFASetup}
                    disabled={twoFABusy}
                    className="px-5 py-2 bg-emerald-500 text-zinc-950 font-cinzel uppercase tracking-wide text-sm hover:bg-emerald-400 disabled:opacity-50 rounded-sm"
                  >
                    {twoFABusy ? "Loading..." : "Set Up 2FA"}
                  </button>
                </>
              ) : (
                <>
                  {/* Setup Flow */}
                  <div className="flex flex-col sm:flex-row gap-5 items-start">
                    <div className="shrink-0">
                      <img
                        src={twoFASetup.qr}
                        alt="Scan with your authenticator app"
                        className="w-44 h-44 bg-white p-2 rounded-sm"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-zinc-400 mb-1">
                        1. Scan this QR code with Google Authenticator or enter the key manually.
                      </p>
                      <p className="text-xs text-zinc-500 mb-3 break-all">
                        Manual key: <span className="font-mono text-amber-300">{twoFASetup.secret}</span>
                      </p>
                      <label className="text-xs text-zinc-400 block mb-1">
                        2. Enter the 6-digit code to confirm
                      </label>
                      <div className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={twoFACode}
                          onChange={(e) => setTwoFACode(e.target.value)}
                          placeholder="000000"
                          maxLength={6}
                          className="px-3 py-2 bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm rounded-sm tracking-[0.3em] text-center max-w-[160px]"
                        />
                        <button
                          onClick={enableTwoFA}
                          disabled={twoFABusy || !twoFACode.trim()}
                          className="px-5 py-2 bg-emerald-500 text-zinc-950 font-cinzel uppercase tracking-wide text-sm hover:bg-emerald-400 disabled:opacity-50 rounded-sm"
                        >
                          {twoFABusy ? "Enabling..." : "Enable"}
                        </button>
                      </div>
                      <button
                        onClick={() => { setTwoFASetup(null); setTwoFACode(""); }}
                        className="text-xs text-zinc-500 hover:text-amber-400 underline"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-5 border-b border-zinc-800">
        {[["banner", "Banners"], ["sigil", "Sigils"]].map(([id, label]) => (
          <button key={id} data-testid={`cosmetics-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide border-b-2 -mb-px transition-colors ${tab === id ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-500 hover:text-amber-300"}`}>{label}</button>
        ))}
      </div>

      <div className="mb-5">
        <button data-testid="equip-none-btn" onClick={() => equip(null)} disabled={busy === "none" || !equipped}
          className="text-xs px-3 py-1.5 border border-zinc-700 text-zinc-400 hover:border-zinc-500 rounded-sm disabled:opacity-40">
          {equipped ? `Remove equipped ${tab}` : `No ${tab} equipped`}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((item) => {
          const isOwned = owned.includes(item.id);
          const isEquipped = equipped === item.id;
          const rm = RARITY_META[item.rarity];
          return (
            <div key={item.id} data-testid={`cosmetic-${item.id}`}
              className={`border bg-zinc-950 rounded-sm overflow-hidden transition-colors ${isEquipped ? "border-amber-500" : "border-zinc-800 hover:border-zinc-700"}`}>
              <div className="h-24 relative flex items-center justify-center bg-zinc-900">
                {isBanner
                  ? <Banner id={item.id} className="absolute inset-0" />
                  : <Sigil id={item.id} size={76} title={`${item.name} sigil artwork`} />}
              </div>
              <div className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-zinc-100 truncate">{item.name}</span>
                  {isEquipped && <Check size={14} className="text-amber-400 shrink-0" />}
                </div>
                <div className={`text-[10px] uppercase tracking-widest ${rm.text} mb-2`}>{rm.label}</div>
                {isOwned ? (
                  isEquipped ? (
                    <span data-testid={`equipped-${item.id}`} className="block text-center text-xs text-amber-400 font-cinzel uppercase tracking-wide py-1.5">Equipped</span>
                  ) : (
                    <button data-testid={`equip-${item.id}`} onClick={() => equip(item.id)} disabled={busy === item.id}
                      className="w-full text-xs font-cinzel uppercase tracking-wide py-1.5 border border-amber-900/50 text-amber-300 hover:border-amber-500 rounded-sm disabled:opacity-50">Equip</button>
                  )
                ) : (
                  <button data-testid={`buy-${item.id}`} onClick={() => setConfirmPurchase({ item, type: tab })} disabled={busy === item.id || data.gold < item.cost}
                    className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-cinzel uppercase tracking-wide py-1.5 bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed">
                    {data.gold < item.cost ? <Lock size={12} /> : <GoldCoin size={12} />} {item.cost}g
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Purchase Confirmation Modal */}
      {confirmPurchase && (
        <Modal open={true} onClose={() => setConfirmPurchase(null)} title={`Purchase ${confirmPurchase.type === 'banner' ? 'Banner' : 'Sigil'}`}>
          <div className="space-y-4">
            {/* Preview */}
            <div className="border border-zinc-800 rounded-sm overflow-hidden bg-zinc-950">
              <div className="h-24 relative flex items-center justify-center bg-zinc-900">
                {confirmPurchase.type === 'banner'
                  ? <Banner id={confirmPurchase.item.id} className="absolute inset-0" />
                  : <Sigil id={confirmPurchase.item.id} size={64} />}
              </div>
              <div className="p-3 text-center">
                <div className="text-zinc-100 font-cinzel mb-1">{confirmPurchase.item.name}</div>
                <div className={`text-xs uppercase tracking-widest ${RARITY_META[confirmPurchase.item.rarity].text}`}>
                  {RARITY_META[confirmPurchase.item.rarity].label}
                </div>
              </div>
            </div>

            {/* Cost */}
            <div className="text-center">
              <div className="text-sm text-zinc-400 mb-2">Purchase for:</div>
              <div className="flex items-center justify-center gap-2 text-amber-300 text-lg font-cinzel">
                <GoldCoin size={20} className="text-amber-500" />
                {confirmPurchase.item.cost.toLocaleString()} gold
              </div>
              <div className="text-xs text-zinc-500 mt-2">
                Remaining: {(data.gold - confirmPurchase.item.cost).toLocaleString()} gold
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-2 pt-2">
              <button
                data-testid="confirm-purchase-btn"
                onClick={() => buy(confirmPurchase.item)}
                disabled={busy === confirmPurchase.item.id}
                className="flex-1 px-4 py-2.5 text-sm font-cinzel uppercase tracking-wide bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {busy === confirmPurchase.item.id ? "Purchasing..." : "Confirm Purchase"}
              </button>
              <button
                data-testid="cancel-purchase-btn"
                onClick={() => setConfirmPurchase(null)}
                disabled={busy === confirmPurchase.item.id}
                className="px-4 py-2.5 text-sm font-cinzel uppercase tracking-wide border border-zinc-700 text-zinc-400 hover:border-zinc-500 rounded-sm disabled:opacity-40 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Disable 2FA Confirmation Modal */}
      <Modal open={twoFADisableModal} onClose={() => { setTwoFADisableModal(false); setTwoFADisableCode(""); }} title="Disable Two-Factor Authentication">
        <div className="space-y-4">
          <div className="p-3 bg-red-950/20 border border-red-900/50 rounded-sm">
            <p className="text-sm text-red-300">
              ⚠️ Disabling 2FA will make your account less secure. You will no longer need an authenticator code to log in.
            </p>
          </div>

          <p className="text-sm text-zinc-400">
            To confirm, enter a 6-digit code from your authenticator app or one of your backup codes.
          </p>

          <div>
            <label className="text-xs text-zinc-400 block mb-1.5">Authenticator Code or Backup Code</label>
            <input
              type="text"
              value={twoFADisableCode}
              onChange={(e) => setTwoFADisableCode(e.target.value)}
              placeholder="000000"
              maxLength={8}
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 text-zinc-100 text-sm rounded-sm tracking-widest text-center"
              autoFocus
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={disableTwoFA}
              disabled={twoFABusy || !twoFADisableCode.trim()}
              className="flex-1 px-4 py-2.5 bg-red-900 border border-red-700 text-zinc-100 font-cinzel uppercase tracking-wide text-sm hover:bg-red-800 disabled:opacity-40 rounded-sm transition-colors"
            >
              {twoFABusy ? "Disabling..." : "Disable 2FA"}
            </button>
            <button
              onClick={() => { setTwoFADisableModal(false); setTwoFADisableCode(""); }}
              disabled={twoFABusy}
              className="flex-1 px-4 py-2.5 text-sm font-cinzel uppercase tracking-wide border border-zinc-700 text-zinc-400 hover:border-zinc-500 rounded-sm disabled:opacity-40 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
